import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AppInjector } from '../../app-injector';
import { IntegrationItemServiceInf, StatusString } from '../interfaces/integrations';
import { TranslateService } from '@ngx-translate/core';
import { AdminService } from 'src/app/core/services/admin.service';
import { Account, Activity, ActivityPatchOperation, Token } from '@mitel/cloudlink-sdk';
import { SpinnerService } from './spinner.service';
import { ConfirmationDialogComponent } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogs } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { AccountsService } from './accounts.service';
import { RBACAuthGuardService } from '../guards/rbac-auth-guard.service';
import { FeatureToggleService } from './feature-toggle.service';
import { TwilioDialogComponent } from '../../modules/integrations-applications/components/integ-config-dialogs/twilio-dialog/twilio-dialog.component'
import { WorkflowService } from './workflow.service';
import { AuthService } from './auth.service';
import { ChatService } from './chat.service';
import _ from 'lodash';
import { InformationDialogComponent } from 'src/app/shared/components/dialogs/information-dialog/information-dialog.component';
import { InformationDialogs } from 'src/app/shared/components/dialogs/information-dialog/information-dialogs.enum';


@Injectable({
	providedIn: 'root'
})

// this service will hide whatever implementation we eventually have for getting the list of integrations
// types. Right now it is static
export class IntegTwilioService implements IntegrationItemServiceInf {

	private adminService: AdminService;
	private translateService: TranslateService;
	private spinnerService: SpinnerService;
	public dialog: MatDialog;
	private accountsService: AccountsService;
	private rbacAuthGuardService: RBACAuthGuardService;
	private featureToggleService: FeatureToggleService;
	private workflowService: WorkflowService;
	private authService: AuthService;
	private chatService: ChatService

	readonly miccTag = 'MICCB_TWILIO';

	readonly micollabTag = 'MICOLLAB_TWILIO';
	readonly micollabSmsOutboundFlowId_global = 'global_c7235677-5e16-40f3-a38c-e3469719b04c';
	readonly micollabSmsAccountAdminFlowId_Global = 'global_1a73284c-3044-4f60-980f-98877e0296a5';
	readonly micollabSmsUserAdminFlowId_Global = 'global_40c64fc4-068f-4839-bc6d-eb41dab1d077';
	readonly micollabSmsInboundFlowId_Global = 'global_ff33d074-ff06-4c81-bc56-0e374473aaf1'

	constructor() {
	}

	initServices() {
		if (!this.adminService)
			this.adminService = AppInjector.injector.get(AdminService);

		if (!this.translateService)
			this.translateService = AppInjector.injector.get(TranslateService);

		if (!this.spinnerService)
			this.spinnerService = AppInjector.injector.get(SpinnerService);

		if (!this.accountsService)
			this.accountsService = AppInjector.injector.get(AccountsService);

		if (!this.dialog)
			this.dialog = AppInjector.injector.get(MatDialog);

		if (!this.rbacAuthGuardService)
			this.rbacAuthGuardService = AppInjector.injector.get(RBACAuthGuardService);

		if (!this.featureToggleService)
			this.featureToggleService = AppInjector.injector.get(FeatureToggleService);

		if (!this.workflowService)
			this.workflowService = AppInjector.injector.get(WorkflowService);

		if (!this.authService)
			this.authService = AppInjector.injector.get(AuthService);

		if (!this.chatService)
			this.chatService = AppInjector.injector.get(ChatService);
	}

	getConfigurationDialog(): any {
		return TwilioDialogComponent;
	}

	isSetupComplete(account: Account): Promise<boolean> {
		this.initServices();
		return this.adminService.getAccountTags(account.accountId).then(tags => {
			if (tags && tags.Twilio) {
				if (tags.Twilio.enabled == true && tags.Twilio.connectionId !== undefined)
					return true;
				return false;
			}
			else
				return false;
		})
			.catch(err => {
				return false;
			});
	}

	handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[]): Promise<any> {
		this.initServices();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
		this.spinnerService.showSpinner();
		if (state === false) {
			return this.adminService.deleteAccountTag(account.accountId, 'Twilio').then(async data => {
				await this.deleteMiCollabSMS(account.accountId);
				await this.workflowService.deleteActivitiesByTagId(this.miccTag);
				this.spinnerService.hideSpinner();
				return;
			})
				.catch(async err => {
					// an error is thrown if no tag exists. That is OK!
					if (err.statusCode === 404) {
						await this.deleteMiCollabSMS(account.accountId);
						await this.workflowService.deleteActivitiesByTagId(this.miccTag);
					}
					this.spinnerService.hideSpinner();
					return;
				})
		}
		else {
			// this is a tri state integration
			let dataChanges = {
				tags: {
					Twilio: {
						enabled: true
					}
				}
			}

			return this.adminService.updateAccount(account.accountId, dataChanges).then(result => {
				this.accountsService.mergeAccountWithModel(result);
				this.spinnerService.hideSpinner(); //bizarre that this needs to be done as the service does not do it
			});
		}
	}

	getStatusString(account: Account): Promise<StatusString> {
		return undefined;
	}

	getConfigButtonLabel(account: Account): Promise<string> {
		return undefined;
	}

	isButtonDisabled(account: Account): Promise<boolean> {
		return Promise.resolve(false);
	}

	canDeleteIntegration(account: Account): Promise<boolean> {
		let canDelete: boolean = undefined;  // do not init
		// must confirm removal
		return this.isSetupComplete(account).then(async isSetup => {
			if (isSetup === false) {
				return true; //there was nothing setup, so delete does not need to be confirmed
			}
			const activePhones = await this.getActiveStreams();
			let dialogRef;
			if (activePhones.length > 0) {
				const messages: string[] = [];
				if (activePhones.length > 1) {
					// plural message
					const message = this.translateService.instant('integrations.twilio.there-are-active-streams', { numPhones: activePhones.length });
					const phones = activePhones.slice(0, 3).join(', ') + ((activePhones.length > 3) ? ' ...' : '');   // short list of phones
					messages.push(message);
					messages.push(phones);
				} else {
					// singular
					const message = this.translateService.instant('integrations.twilio.there-is-an-active-stream', { phoneNumber: activePhones[0] });
					messages.push(message);
				}
				dialogRef = this.dialog.open(InformationDialogComponent, { data: { mode: InformationDialogs.TWILIO_ACTIVE_STREAMS, additionalMessages: messages} });
				canDelete = false;  // can not delete; there are active phones
			} else {
				dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.REMOVE_TWILIO_CONFIRMATION } });
			}

			//display a dialog to so the user can confirm the deletion
			return dialogRef.afterClosed().toPromise().then(result => {
				// if canDelete is predetermined, ensure that we return it
				return canDelete ?? result;
			});
		});
	}

	isIntegrationStateOn(account: Account): Promise<boolean> {
		this.initServices();
		return this.adminService.getAccountTags(account.accountId).then(tags => {
			if (tags && tags.Twilio && tags.Twilio.enabled != undefined)
				return tags.Twilio.enabled;
			else
				return false;
		})
			.catch(err => {
				return false;
			})
	}

	canBeSelected(account: Account): Promise<boolean> {
		return Promise.resolve(true);
	}

	async completeSetup(accountId: string, connectionId: string) {
		this.initServices();
		let dataChanges = {
			tags: {
				Twilio: {
					enabled: true,
					connectionId
				}
			}
		}
		await this.setupMiCollabSMS(accountId, connectionId);
		const account = await this.adminService.updateAccount(accountId, dataChanges);
		await this.accountsService.mergeAccountWithModel(account);
		return account;
	}

	async setupMiCollabSMS(accountId: string, twilioConnectionId: string) {
		this.initServices();
		const providerId = await this.getSMSProviderClientId(accountId);

		if (!providerId) {
			// create tag
			await this.createMiCollabTag();

			// delete existing activities
			await this.workflowService.deleteActivitiesByTagId(this.micollabTag);

			// clone flows
			const { accountAdminFlow, userAdminFlow, inboundFlow } = await this.cloneMiCollabFlows();

			// create sms provider clientId
			const client = await this.adminService.createClient(accountId, 'Provider', 'ACCOUNT_ADMIN');

			// set run as identity
			await this.setRunAsIdentityForMiCollabFlows(accountAdminFlow.id, userAdminFlow.id, inboundFlow.id, client.clientId, client.clientSecret);

			// configure variables
			await this.configureVariablesForMiCollabSMS(userAdminFlow.id, accountAdminFlow.id, inboundFlow.id, this.micollabSmsOutboundFlowId_global, client.clientId, client.clientSecret, twilioConnectionId);

			// delete commands if exist
			await this.deleteMiCollabSmsCommands();

			// configure commands
			await this.configureMiCollabSmsCommands(userAdminFlow.id, accountAdminFlow.id);

			// create account sms integration tag
			await this.adminService.createAccountSmsIntegrationTag(accountId, client.clientId, this.micollabSmsOutboundFlowId_global, inboundFlow.id, userAdminFlow.id, accountAdminFlow.id, []);

			console.log('SMS enabled for Micollab successfully');

		} else {
			console.log(`SMS is already configured on the account.`);
		}
	}

	async updateMiCollabSMSAdministrators(accountId: string, userIds: string[], updateIsAdd: boolean) {
		if (updateIsAdd) {
			return this.addMiCollabSMSAdministrators(accountId, userIds);
		} else {
			return this.removeMiCollabSMSAdministrators(accountId, userIds);
		}
	}

	async addMiCollabSMSAdministrators(accountId: string, userIds: string[]) {
		this.initServices();
		return this.adminService.getAccountTags(accountId).then(tags => {
			if (tags?.smsIntegration) {
				const smsAdministrators = tags.smsIntegration.smsAdministrators || [];
				let updatedTags = { smsIntegration: _.cloneDeep(tags.smsIntegration) };
				if (smsAdministrators.length == 0) {
					updatedTags.smsIntegration['smsAdministrators'] = userIds;
				} else {
					updatedTags.smsIntegration.smsAdministrators = _.union(smsAdministrators, userIds);
				}
				return this.adminService.tryUpdateAccountTags(accountId, tags, updatedTags);
			}
		})
		.catch(err => {
			console.log('There was a problem fetching the account tags', JSON.stringify(err));
		});
	}

	async removeMiCollabSMSAdministrators(accountId: string, userIds: string[]) {
		this.initServices();
		return this.adminService.getAccountTags(accountId).then(tags => {
			if (tags?.smsIntegration) {
				const smsAdministrators = tags.smsIntegration.smsAdministrators || [];
				let updatedTags = { smsIntegration: _.cloneDeep(tags.smsIntegration) };
				updatedTags.smsIntegration.smsAdministrators = _.difference(smsAdministrators, userIds);
				return this.adminService.tryUpdateAccountTags(accountId, tags, updatedTags);
			}
		})
		.catch(err => {
			console.log('There was a problem fetching the account tags', JSON.stringify(err));
		});
	}

	async deleteMiCollabSMS(accountId: string) {
		this.initServices();
		await this.deleteMiCollabSMSClientId(accountId);
		await this.deleteMiCollabSmsWorkflows(accountId);
		await this.adminService.deleteAccountTag(accountId, 'smsIntegration')
			.catch(async err => {
				// an error is thrown if no tag exists. That is OK!
				if (err.statusCode !== 404) {
					throw err;
				}
			});
	}

	async deleteMiCollabSMSClientId(accountId: string) {
		this.initServices();
		const tags = await this.adminService.getAccountTags(accountId);
		const smsClientId = tags.smsIntegration?.smsProviderClientId;
		if (smsClientId) {
			await this.adminService.deleteClientById(smsClientId)
				.catch((error) => {
					if (error.statusCode !== 404) throw error;
				});
		}
	}

	async deleteMiCollabSmsWorkflows(accountId: string) {
		this.initServices();
		const tags = await this.adminService.getAccountTags(accountId);
		const integrationTag = tags.smsIntegration;
		if (integrationTag) {
			await this.workflowService.deleteActivityById(integrationTag.smsInboundFlowId);
			await this.workflowService.deleteActivityById(integrationTag.smsUserAdminFlowId);
			await this.workflowService.deleteActivityById(integrationTag.smsAccountAdminFlowId);
		}
		await this.workflowService.deleteActivitiesByTagId(this.micollabTag)
			.catch(error => {
				if (error.statusCode !== 404) throw error;
			});
	}

	async getSMSProviderClientId(accountId: string) {
		this.initServices();
		return this.adminService.getAccountTags(accountId).then(tags => {
			if (tags && tags.smsIntegration)
				return tags.smsIntegration.smsProviderClientId;
			else
				return undefined;
		})
			.catch(() => {
				return undefined;
			});
	}

	async createMiCollabTag() {
		this.initServices();
		await this.workflowService.getTag(this.micollabTag)
			.catch(async (error) => {
				if (error.statusCode === 404) {
					await this.workflowService.createTag(this.micollabTag, 'MiCollab Twilio SMS', '#C7CED9');
				}
			});
	}

	async cloneMiCollabFlows() {
		this.initServices();
		const ret = await Promise.all([
			this.cloneAndLockHideActivity(this.micollabSmsAccountAdminFlowId_Global, 'Cloned MiCollab SMS Account Admin Flow'),
			this.cloneAndLockHideActivity(this.micollabSmsUserAdminFlowId_Global, 'Cloned MiCollab SMS User Admin Flow'),
			this.cloneAndLockHideActivity(this.micollabSmsInboundFlowId_Global, 'Cloned MiCollab SMS Inbound Flow')
		]);

		return {
			accountAdminFlow: ret[0],
			userAdminFlow: ret[1],
			inboundFlow: ret[2]
		}
	}

	async setRunAsIdentityForMiCollabFlows(accountAdminFlowId: string, userAdminFlowId: string, inboundFlowId: string, clientId: string, clientSecret: string) {
		this.initServices();
		const clientToken = await this.authService.createTokenForClient(clientId, clientSecret);

		const clientIdentityOp: ActivityPatchOperation = {
			op: 'REPLACE',
			path: '/connectors',
			value: {
				cloudlink_identity: {
					$type: 'CloudlinkIdentity',
					id: clientId
				}
			}
		};

		const callerIdentityOp: ActivityPatchOperation = {
			op: 'REPLACE',
			path: '/connectors',
			value: {
				cloudlink_identity: {
					$type: 'CloudlinkCaller',
					id: '[caller]'
				}
			}
		};

		await Promise.all([
			this.workflowService.updateActivityAttributesUsingTokenProvided(clientToken.access_token, accountAdminFlowId, [clientIdentityOp]),
			this.workflowService.updateActivityAttributesUsingTokenProvided(clientToken.access_token, userAdminFlowId, [callerIdentityOp]),
			this.workflowService.updateActivityAttributesUsingTokenProvided(clientToken.access_token, inboundFlowId, [clientIdentityOp])
		]);
	}

	async configureVariablesForMiCollabSMS(userAdminFlowId: string, accountAdminFlowId: string, inboundFlowId: string, outboundFlowId: string, clientId: string, clientSecret: string, twilioConnectionId: string) {
		this.initServices();
		const userAndAccountAdminFlowPatchOps: ActivityPatchOperation[] = [
			{
				op: 'REPLACE',
				path: '/variables/ClientID',
				value: {
					valueType: 'String',
					description: null,
					default: clientId,
					isSecure: false
				}
			},
			{
				op: 'REPLACE',
				path: '/variables/ClientSecret',
				value: {
					valueType: 'String',
					description: null,
					default: clientSecret,
					isSecure: true
				}
			},
			{
				op: 'REPLACE',
				path: '/variables/TwilioInboundFlowId',
				value: {
					valueType: 'String',
					description: null,
					default: inboundFlowId,
					isSecure: false
				}
			},
			{
				op: 'REPLACE',
				path: '/variables/TwilioOutboundFlowId',
				value: {
					valueType: 'String',
					description: null,
					default: outboundFlowId,
					isSecure: false
				}
			}
		];

		const inboundFlowPatchOps: ActivityPatchOperation[] = [
			{
				op: 'REPLACE',
				path: '/trigger/throughputs/Connection/expression',
				value: twilioConnectionId
			},
			{
				op: 'REPLACE',
				path: '/tags',
				value: [
					this.micollabTag
				]
			}
		];

		await Promise.all([
			this.workflowService.updateActivityAttributes(userAdminFlowId, userAndAccountAdminFlowPatchOps),
			this.workflowService.updateActivityAttributes(inboundFlowId, inboundFlowPatchOps),
			this.workflowService.updateActivityAttributes(accountAdminFlowId, userAndAccountAdminFlowPatchOps)
		]);
	}

	async deleteMiCollabSmsCommands() {
		this.initServices();
		const smsAddCommand = 'sms-add';
		const smsAdminCommand = 'sms-admin';

		await Promise.all([
			this.chatService.deleteCommand(smsAddCommand)
				.catch((error) => { if (error.statusCode !== 404) throw error; }),

			this.chatService.deleteCommand(smsAdminCommand)
				.catch((error) => { if (error.statusCode !== 404) throw error; })
		]);
	}

	async configureMiCollabSmsCommands(userAdminFlowId: string, accountAdminFlowId: string) {
		this.initServices();
		await Promise.all([
			this.chatService.createCommand({
				commandId: 'sms-add',
				description: 'Execute sms-add command',
				parameters: [
					{
						parameterId: 'ConversationId',
						description: 'ConversationId',
						required: true
					},
					{
						parameterId: 'provider',
						description: 'provider',
						required: true
					},
					{
						parameterId: 'number',
						description: 'number',
						required: true
					}
				],
				referenceCommandId: 'workflow',
				referenceCommandParameters: [
					{
						parameterId: 'workflow',
						value: userAdminFlowId
					},
					{
						parameterId: 'arguments',
						value: 'ConversationId={{ConversationId}} provider={{provider}} number={{number}}'
					}
				]
			}).catch((err) => {
				if (err.statusCode !== 409) throw err;
			}),

			this.chatService.createCommand({
				commandId: 'sms-admin',
				description: 'Execute sms-admin command',
				parameters: [
					{
						parameterId: 'conversationId',
						description: 'conversationId',
						required: true
					},
					{
						parameterId: 'operation',
						description: 'operation',
						required: true
					},
					{
						parameterId: 'username',
						description: 'username',
						required: true
					},
					{
						parameterId: 'stream',
						description: 'stream',
						required: true
					}
				],
				referenceCommandId: 'workflow',
				referenceCommandParameters: [
					{
						parameterId: 'workflow',
						value: accountAdminFlowId
					},
					{
						parameterId: 'arguments',
						value: 'conversationId={{ConversationId}} operation={{operation}} username={{username}} stream={{stream}}'
					}
				]
			}).catch((err) => {
				if (err.statusCode !== 409) throw err;
			})
		]);
	}

	async cloneAndLockHideActivity(cloneFromActivityId: string, displayName: string) {
		this.initServices();
		const activity = await this.workflowService.cloneActivity(cloneFromActivityId, displayName);

		return this.workflowService.updateActivityAttributes(activity.id, [
			{
				"op": "REPLACE",
				"path": "/active",
				"value": "0.0.1"
			},
			{
				"op": "REPLACE",
				"path": "/lock",
				"value": true
			},
			{
				"op": "REPLACE",
				"path": "/visibility",
				"value": "Hidden"
			}
		]);
	}

	/**
	 * Active Stream - is true when there is ConversationId populated against a one or more phone numbers in the MiCollab Phone Numbers.
	 * @returns Promise Array of phone numbers that have active streams
	 */
	getActiveStreams(): Promise<string[]> {
		return this.workflowService.getActivitiesByTagId(this.micollabTag).then(
			activities => {
				const promises: Promise<Activity>[] = [];
				activities?._embedded?.items?.forEach(i => promises.push(this.workflowService.getActivityById(i.id)));
				return promises.length ? Promise.all(promises).then(
					// active stream is when any phoneNumber's associated conversationId is populated
					activities => activities
						// if it has a variables.PhoneNumbers.default array
						.filter(a => a?.variables?.PhoneNumbers?.default)
						// AND  a phoneNumber array entry with at least 1 conversationId
						.filter(a => a.variables.PhoneNumbers.default.filter(p => !!p.conversationId && !!p.phoneNumber))
						// return just the phoneNumber
						.flatMap(p => p.variables.PhoneNumbers.default.map(p => !!p.conversationId ? p.phoneNumber : undefined))
						// remove any undefined entries
						.filter(phoneNumbers => !!phoneNumbers)
				) : [];
			}
		)

	}
}
